import { FlowAction } from '@/@types/Batch';
import { IBatchDebitNote } from '@/@types/BatchDebitNote';
import { zodResolver } from '@hookform/resolvers/zod';
import { useIsFetching } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { MousePointerClick, Search } from 'lucide-react';
import { useEffect, useLayoutEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AlertModal } from '@/common/components/AlertModal';
import Button from '@/common/components/Button';
import { TextInput } from '@/common/components/TextInput';
import { parseJWT } from '@/common/utils/parseJWT';
import {
  TableSearchForm,
  TableSearchSchema,
} from '@/modules/dealers/schemas/TableSearchSchema';
import { useMassSelection } from '@/modules/invoicing/hooks/useMassSelection';
import {
  MassSelectionSchema,
  MassSelectionForm,
} from '@/modules/invoicing/schemas/MassSelectionSchema';

import * as styles from './styles';
interface IReviewToken {
  email: string;
  userId: string;
  accountId: string;
  flow: FlowAction;
  isOperator?: boolean;
  operatorName?: string;
}

type TableSearchProps = {
  onSearch: (search: string) => void;
  selectedBatchDebitNotes: IBatchDebitNote[];
};

export function TableSearch({
  onSearch,
  selectedBatchDebitNotes,
}: TableSearchProps) {
  const { t } = useTranslation(['invoicing/batchInvoices', 'common']);
  const { updateCodes } = useMassSelection();
  const hookForm = useForm<TableSearchForm>({
    mode: 'onSubmit',
    resolver: zodResolver(TableSearchSchema()),
  });
  const massSelectionHookForm = useForm<MassSelectionForm>({
    mode: 'onBlur',
    resolver: zodResolver(MassSelectionSchema()),
    defaultValues: {
      massSelection: '',
    },
  });
  const isFetchingInvoices = useIsFetching({
    queryKey: ['infiniteBatchDebitNote'],
    exact: false,
  });

  const searchInputValue = hookForm.watch('search');
  const jwtToken = Cookies.get('jwt-access-batch');
  const jwtPayload = parseJWT<IReviewToken>(jwtToken);

  const loadAndSelectSAPCodes = (data: MassSelectionForm) => {
    updateCodes(data.massSelection);
    massSelectionHookForm.reset();
  };

  useEffect(() => {
    onSearch(searchInputValue);
  }, [searchInputValue, onSearch]);

  useLayoutEffect(() => {
    window.HSStaticMethods.autoInit();
  }, [selectedBatchDebitNotes.length]);

  return (
    <div className={styles.container()}>
      <FormProvider {...hookForm}>
        <form className={styles.content()}>
          <TextInput
            name="search"
            LeadingIcon={
              <Search
                className={styles.searchInputIcon()}
                size={18}
                strokeWidth={2.5}
              />
            }
            placeholder={t('common:search')}
          />
        </form>
      </FormProvider>

      <div className={styles.actions()}>
        {jwtPayload?.flow === 'COMPLIANCE' && (
          <Button
            variant="primary"
            className={styles.dropButton()}
            type="button"
            text={t('massSelection')}
            leadingIcon={<MousePointerClick size={16} />}
            data-hs-overlay="#hs-modal-mass-selection"
          />
        )}
      </div>

      <FormProvider {...massSelectionHookForm}>
        <AlertModal
          type="primary"
          id="mass-selection"
          title={t('massSelection')}
          description={
            <div className="flex w-full flex-col items-center justify-center gap-2">
              <p>{t('massSelectionDescription')}</p>

              <TextInput
                name="massSelection"
                placeholder={t('massSelectionPlaceholder')}
              />
            </div>
          }
          confirmText={t('common:confirm')}
          cancelText={t('common:cancel')}
          onConfirm={massSelectionHookForm.handleSubmit(loadAndSelectSAPCodes)}
          onCancel={() => massSelectionHookForm.reset()}
          isLoading={isFetchingInvoices > 0}
        />
      </FormProvider>
    </div>
  );
}
